import type { FC } from 'react';
import { Controller } from 'react-hook-form';
import InputSelect from '@components/InputSelect';
import InputText from '@components/InputText';
import { Show } from '@components/Show';
import type { Team } from '@domain/schemas/team.schema';
import type { Step } from '@features/create-entity-modal/types';
import { ALL_CLASSES_OF_BUSINESS } from '@root/helpers/constants';
import { isPPLFrontend } from '@utils/app-paths';

import type { StepProps } from '../types';

interface Step1Props extends StepProps {
  currentStep: Step;
  userTeams: Team[];
}

const getRequiredMessage = (message: string = '') => {
  return message === 'Required' ? 'A contract requires a name' : message;
};

export const Step1: FC<Step1Props> = ({ control, formValues, userTeams }) => {
  return (
    <div className="flex flex-col gap-6">
      <Controller
        control={control}
        render={({ field, fieldState: { error } }) => (
          <InputText
            {...field}
            errors={getRequiredMessage(error?.message)}
            isDisabled={isPPLFrontend}
            readOnly={isPPLFrontend}
            labelText="Contract name"
            placeholder="New Contract"
          />
        )}
        name="name"
      />
      <Controller
        control={control}
        render={({ field, fieldState: { error } }) => (
          <InputSelect
            {...field}
            data-testid="select:assign-to-team"
            errors={error?.message}
            isDisabled={isPPLFrontend}
            onChange={(value) => field.onChange([value])}
            labelText="Contract team"
            options={userTeams.map(({ id, name }) => ({ name, value: id }))}
            value={field.value?.[0]}
          />
        )}
        name="team"
      />
      <Show when={!isPPLFrontend}>
        <Controller
          control={control}
          render={({ field, fieldState: { error } }) => (
            <InputSelect
              {...field}
              errors={error?.message}
              isClearable
              labelText="Class of business"
              options={ALL_CLASSES_OF_BUSINESS}
              placeholder="Select the class of business"
              // Getting the value from `formValues` as opposed to `Controller` since it does a weird caching which breaks the `Clear selection` logic
              value={formValues?.class_of_business}
              dataTestId="select:class-of-business"
            />
          )}
          name="class_of_business"
        />
      </Show>
    </div>
  );
};
